import {MAX_MAIL_ATTACHMENT_BYTES, MAX_MAIL_ATTACHMENT_MB} from '@artemis-group/aiw-contracts';
/** Open the Proposal as the PDF the customer email carries, drawn by the backbone (`/api/proposals/:id/pdf`).
 *
 *  Jacky, 14 October 2026, looking at Tony's Proposal in the desktop app: *"where the hell is share step"* — the
 *  Print / save PDF link lived inside the Send / private link tab, and only once that browser held the customer's
 *  private link. A PDF needs nothing but the Proposal's id, so this is one call any screen can make: the Job's
 *  Current saved Proposal box and the Send tab both use it. A blob URL goes through `window.open`, which the desktop
 *  shell routes to the Mac's viewer and a browser opens in a tab. Dependencies are passed in so it tests without a DOM. */
export type PdfOutcome = {ok: true} | {ok: false; reason: string; blocked?: boolean};
export async function openProposalPdf(proposalId: string, deps: {
  apiBase: string;
  headers: () => Promise<Record<string, string>>;
  fetch: (url: string, init: RequestInit) => Promise<{ok: boolean; status: number; headers: {get(name: string): string | null}; blob(): Promise<Blob>; json(): Promise<unknown>}>;
  open: (url: string) => unknown;
  createUrl?: (blob: Blob) => string;
  revokeUrl?: (url: string) => void;
}): Promise<PdfOutcome> {
  if (!/^[0-9a-f-]{36}$/i.test(proposalId)) return {ok: false, reason: 'Not a Proposal'};
  const r = await deps.fetch(`${deps.apiBase}/api/proposals/${encodeURIComponent(proposalId)}/pdf`, {headers: await deps.headers()});
  if (!r.ok || r.headers.get('content-type') !== 'application/pdf') {
    const body = await r.json().catch(() => ({})) as {error?: string};
    return {ok: false, reason: body.error ?? `pdf ${r.status}`};
  }
  const url = (deps.createUrl ?? URL.createObjectURL)(await r.blob());
  const opened = deps.open(url);
  // The shell's bridge returns null from window.open after it has taken the bytes, so null alone is not a block;
  // a browser popup blocker returns null too and shows nothing. The caller tells the viewer what happened.
  setTimeout(() => (deps.revokeUrl ?? URL.revokeObjectURL)(url), 60_000);
  return opened === null && typeof window !== 'undefined' && !('__TAURI_INTERNALS__' in window) ? {ok: false, reason: 'blocked', blocked: true} : {ok: true};
}

/** The same PDF, as a file to ATTACH to the Proposal email (report 89bffef3, 16 Oct 2026: "send to customer with the
 *  URL and attachment, so both options is provided"). It is the released copy the link opens, drawn by the backbone —
 *  never a second renderer. Refused over the per-file ceiling a mail attachment may be (3 MB, @artemis-group/aiw-contracts), so the send never fails at the mailbox. */
export type PdfFile = {ok: true; name: string; size: number; content_base64: string} | {ok: false; reason: string};
export const MAX_ATTACHMENT_BYTES = MAX_MAIL_ATTACHMENT_BYTES;
export function toBase64(bytes: Uint8Array): string { let out = ''; for (let i = 0; i < bytes.length; i += 16383) out += btoa(String.fromCharCode(...bytes.subarray(i, i + 16383))); return out; }
export async function fetchProposalPdf(proposalId: string, name: string, deps: {
  incompleteMessage?:string;
  /** Which document: the Proposal (`pdf`) or its strata scope pack (`strata-pack`, report 89bffef3). */
  document?: 'pdf' | 'strata-pack';
  /** Cancels the render when a newer version supersedes it (lib/pdf-attachment.ts). */
  signal?: AbortSignal;
  apiBase: string;
  headers: () => Promise<Record<string, string>>;
  fetch: (url: string, init: RequestInit) => Promise<{ok: boolean; status: number; headers: {get(name: string): string | null}; arrayBuffer(): Promise<ArrayBuffer>; json(): Promise<unknown>}>;
}): Promise<PdfFile> {
  if (!/^[0-9a-f-]{36}$/i.test(proposalId)) return {ok: false, reason: 'Not a Proposal'};
  const r = await deps.fetch(`${deps.apiBase}/api/proposals/${encodeURIComponent(proposalId)}/${deps.document ?? 'pdf'}`, {headers: await deps.headers(), ...(deps.signal ? {signal: deps.signal} : {})});
  if (!r.ok || r.headers.get('content-type') !== 'application/pdf') {
    const body = await r.json().catch(() => ({})) as {error?: string;code?:string;missing?:string[]};
    if(body.code==='strata_incomplete')return {ok:false,reason:deps.incompleteMessage??'Strata pack is incomplete. Check the acoustic report and insurance documents.'};
    return {ok: false, reason: body.error ?? `pdf ${r.status}`};
  }
  const bytes = new Uint8Array(await r.arrayBuffer());
  if (bytes.length > MAX_ATTACHMENT_BYTES) return {ok: false, reason: `the PDF is ${(bytes.length / 1024 / 1024).toFixed(1)} MB; an attachment may be ${MAX_MAIL_ATTACHMENT_MB} MB`};
  return {ok: true, name, size: bytes.length, content_base64: toBase64(bytes)};
}
